import { useCallback, useEffect, useRef, useState } from 'react';

import {
  DndContext,
  DragEndEvent,
  DragOverEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { useTranslation } from 'react-i18next';

import {
  IncubatorMapSlot,
  useGenerateIncubatorMapsMutation,
  useGetIncubatorMapsQuery,
  useLazyValidateMapMoveQuery,
  useReorderIncubatorMapsMutation,
  useToggleSlotDisabledMutation,
} from '@/app/services/fertWork';
import { PageStatusBanner } from '@/components/common';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  useFertWorkMapsReport,
  useFertWorkSingleMapReport,
} from '@/services/reports';
import { useToast } from '@/services/toast';
import { EMBRYO_CATEGORY_COLOR_KEY_ITEMS } from '@/utils/categoryColors';

import { useIncubatorSelection } from '../../hooks/useIncubatorSelection';
import { formatUtcDateAsLocal } from '../../utils/dateHelpers';
import { getStoredIncubatorSelection } from '../../utils/incubatorSelection';
import { IncubatorSelectionFields } from '../IncubatorSelectionFields';
import { useRegenerateConfirm } from '../RegenerateConfirm';
import { DraggableSlot } from './DraggableSlot';
import { DroppableSlot } from './DroppableSlot';
import { SlotContent } from './SlotContent';

const MAP_SLOT_HEIGHT_CLASS = 'min-h-[132px] sm:min-h-[148px]';

const GRID_DIMENSIONS: Record<string, { rows: number; cols: number }> = {
  ImvPlaner: { rows: 2, cols: 4 },
  IvfTech: { rows: 4, cols: 6 },
  // WI 36063 (behind fert_work_additional_incubator_types): ImvPlaner/IvfTech renamed,
  // plus 2 brand-new chamber layouts.
  '2 Chamber (horizontal)': { rows: 2, cols: 4 },
  '6 Chamber': { rows: 4, cols: 6 },
  '2 Chamber (vertical)': { rows: 6, cols: 2 },
  '3 Chamber': { rows: 2, cols: 6 },
};

function RegenerateMapsDropdown({
  onRegenerate,
  isLoading,
  labelCount,
  sessionId,
}: {
  onRegenerate: (
    incubatorCounts: Record<string, number>,
    preferenceOrder: string[],
  ) => void;
  isLoading: boolean;
  labelCount: number;
  sessionId: string;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const incubatorSelection = useIncubatorSelection(labelCount, sessionId);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node))
        setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleRegenerate = (e: React.MouseEvent) => {
    e.stopPropagation();
    setOpen(false);
    const { incubatorCounts, preferenceOrder } =
      incubatorSelection.buildSelection();
    onRegenerate(incubatorCounts, preferenceOrder);
  };

  const handleClick = () => {
    if (isLoading) return;
    const { incubatorCounts, preferenceOrder } = getStoredIncubatorSelection();
    onRegenerate(incubatorCounts, preferenceOrder);
  };

  return (
    <div
      ref={ref}
      className="relative"
      onMouseEnter={() => {
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        if (!isLoading) setOpen(true);
      }}
      onMouseLeave={() => {
        timeoutRef.current = setTimeout(() => setOpen(false), 200);
      }}
    >
      <Button
        variant="warning"
        onClick={handleClick}
        disabled={isLoading}
        className="text-sm"
      >
        <i
          className={isLoading ? 'pi pi-spin pi-spinner' : 'pi pi-refresh'}
          data-icon="inline-start"
        />
        {t('fertWork.regenerate', 'Regenerate')}
      </Button>
      {open && (
        <div className="absolute top-full left-0 z-50 mt-1 w-64 rounded-md border border-gray-200 bg-white p-3 shadow-lg">
          <IncubatorSelectionFields
            selection={incubatorSelection}
            labelCount={labelCount}
          />
          <button
            type="button"
            onClick={handleRegenerate}
            disabled={
              incubatorSelection.activeTypeNames.length === 0 ||
              incubatorSelection.hasCapacityError
            }
            className={`w-full rounded border-0 px-3 py-1.5 text-sm font-medium ${
              incubatorSelection.activeTypeNames.length === 0 ||
              incubatorSelection.hasCapacityError
                ? 'cursor-not-allowed bg-gray-300 text-gray-500'
                : 'bg-cyan-600 text-white hover:bg-cyan-700'
            }`}
          >
            {t('fertWork.regenerate', 'Regenerate')}
          </button>
        </div>
      )}
    </div>
  );
}

export type GenerateIncubatorMapsProps = {
  sessionId: string;
  className?: string;
};

export const GenerateIncubatorMaps = ({
  sessionId,
  className = '',
}: GenerateIncubatorMapsProps) => {
  const { t } = useTranslation();
  const [showRules, setShowRules] = useState(false);
  const [activeSlot, setActiveSlot] = useState<IncubatorMapSlot | null>(null);
  const [overPosition, setOverPosition] = useState<string | null>(null);
  const [showFab, setShowFab] = useState(false);
  const actionsRowRef = useRef<HTMLDivElement>(null);
  const toast = useToast();
  const showRegenerateConfirm = useRegenerateConfirm();

  const matingsPerPlate = parseInt(
    localStorage.getItem('fertwork-matings-per-plate') || '2',
  );

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 200,
        tolerance: 5,
      },
    }),
  );

  const {
    data: mapData,
    isLoading,
    error,
    refetch,
  } = useGetIncubatorMapsQuery(sessionId, {
    skip: !sessionId,
  });

  const isCommercial = mapData?.sessionType === 1;

  const [generateMaps, { isLoading: isGenerating, error: generateError }] =
    useGenerateIncubatorMapsMutation();

  const [reorderMaps] = useReorderIncubatorMapsMutation();
  const [toggleSlotDisabled, { isLoading: isTogglingSlot }] =
    useToggleSlotDisabledMutation();
  const [validateMove] = useLazyValidateMapMoveQuery();

  const handleToggleSlotDisabled = async (slotId: string) => {
    try {
      await toggleSlotDisabled({ sessionId, slotId }).unwrap();
    } catch (err: any) {
      toast.show({
        severity: 'error',
        summary: t('fertWork.toasts.invalidMove', 'Invalid Move'),
        detail:
          err?.data ||
          t(
            'fertWork.toasts.cannotMovePlate',
            'Cannot move plate to this position',
          ),
      });
    }
  };
  const [generateMapsPdf, { isPending: isDownloadingPdf }] =
    useFertWorkMapsReport();
  const [generateSingleMapPdf, { isPending: isDownloadingSinglePdf }] =
    useFertWorkSingleMapReport();
  const [printingIncubator, setPrintingIncubator] = useState<string | null>(
    null,
  );

  useEffect(() => {
    const el = actionsRowRef.current;
    if (!el) {
      setShowFab(false);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => setShowFab(!entry.isIntersecting),
      { threshold: 0 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [!!mapData]);

  const doGenerateMaps = async (
    incubatorCounts?: Record<string, number>,
    preferenceOrder?: string[],
    retainExisting = false,
  ) => {
    const stored = getStoredIncubatorSelection();
    const counts = incubatorCounts ?? stored.incubatorCounts;
    const order = preferenceOrder ?? stored.preferenceOrder;

    try {
      await generateMaps({
        sessionId,
        incubatorCounts: counts,
        preferenceOrder: order,
        retainExisting,
      }).unwrap();
      refetch();
    } catch (err) {
      console.error('Failed to generate maps:', err);
    }
  };

  const handleSettingsRegenerate = useCallback(
    (incubatorCounts: Record<string, number>, preferenceOrder: string[]) => {
      const summary = Object.entries(incubatorCounts)
        .map(([name, count]) => `${name}: ${count}`)
        .join(', ');

      showRegenerateConfirm({
        header: t('fertWork.regenerateMaps', 'Regenerate Maps'),
        description: t(
          'fertWork.regenerateMapsDescription',
          'Choose whether to reset map arrangement or retain current order and manual changes while regenerating ({summary}).',
          { summary },
        ),
        warningTitle: t('fertWork.regenerateOptions', 'Regenerate options'),
        warningDetail: t(
          'fertWork.regenerateKeepOrResetMaps',
          'Reset will rebuild maps from scratch and apply fresh balanced distribution across incubators. Retain will regenerate but keep existing plate positions when possible.',
        ),
        acceptLabel: t('fertWork.resetAndRegenerate', 'Reset and Regenerate'),
        rejectLabel: t('fertWork.retainAndRegenerate', 'Retain and Regenerate'),
        onAccept: () => doGenerateMaps(incubatorCounts, preferenceOrder, false),
        onReject: () => doGenerateMaps(incubatorCounts, preferenceOrder, true),
      });
    },
    [doGenerateMaps],
  );

  const handlePrintMap = async () => {
    try {
      await generateMapsPdf({
        sessionId,
        ...(matingsPerPlate === 4 && { matingsPerPlate: 4 }),
      });
    } catch (err: any) {
      console.error('Failed to print map PDF:', err);
      toast.show({
        severity: 'error',
        summary: t('common.error', 'Error'),
        detail: t('fertWork.toasts.failedToPrintMap', 'Failed to print map'),
      });
    }
  };

  const handlePrintSingleMap = async (incubatorName: string) => {
    try {
      setPrintingIncubator(incubatorName);
      await generateSingleMapPdf({
        sessionId,
        incubatorName,
        ...(matingsPerPlate === 4 && { matingsPerPlate: 4 }),
      });
    } catch (err: any) {
      console.error('Failed to print single map PDF:', err);
      toast.show({
        severity: 'error',
        summary: t('common.error', 'Error'),
        detail: t(
          'fertWork.toasts.failedToPrintMapFor',
          'Failed to print map for {name}',
          {
            name: incubatorName,
          },
        ),
      });
    } finally {
      setPrintingIncubator(null);
    }
  };

  const handleDragStart = (event: DragStartEvent) => {
    const { active } = event;
    const slot = mapData?.slots.find((s) => s.id === active.id);
    if (slot) {
      setActiveSlot(slot);
    }
  };

  const handleDragOver = (event: DragOverEvent) => {
    const { over } = event;
    if (over) {
      setOverPosition(over.id as string);
    } else {
      setOverPosition(null);
    }
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveSlot(null);
    setOverPosition(null);

    if (!over || !activeSlot) return;

    const targetId = over.id as string;
    const [targetIncubator, targetPosition] = targetId.split(':');
    const sourceIncubator = activeSlot.incubatorName;

    if (
      activeSlot.slotPosition === targetPosition &&
      sourceIncubator === targetIncubator
    ) {
      return;
    }

    try {
      const validation = await validateMove({
        sessionId,
        slotId: activeSlot.id,
        targetSlotPosition: targetPosition,
        targetIncubatorName: targetIncubator,
      }).unwrap();

      if (!validation.isValid) {
        toast.show({
          severity: 'error',
          summary: t('fertWork.toasts.invalidMove', 'Invalid Move'),
          detail:
            validation.errorMessage ||
            t(
              'fertWork.toasts.cannotMovePlate',
              'Cannot move plate to this position',
            ),
        });
        return;
      }

      await reorderMaps({
        sessionId,
        moves: [
          {
            slotId: activeSlot.id,
            newSlotPosition: targetPosition,
            targetIncubatorName: targetIncubator,
          },
        ],
      }).unwrap();

      toast.show({
        severity: 'success',
        summary: t('fertWork.toasts.plateMoved', 'Plate Moved'),
        detail: t(
          'fertWork.toasts.plateMovedDetail',
          'Moved plate from {from} to {to}',
          {
            from: activeSlot.slotPosition,
            to: targetPosition,
          },
        ),
      });

      refetch();
    } catch (err) {
      toast.show({
        severity: 'error',
        summary: t('common.error', 'Error'),
        detail: t('fertWork.toasts.failedToMovePlate', 'Failed to move plate'),
      });
    }
  };

  const handleDragCancel = () => {
    setActiveSlot(null);
    setOverPosition(null);
  };

  const renderSlot = (
    slot: IncubatorMapSlot | undefined,
    position: string,
    incubatorName: string,
    dataTour?: string,
  ) => {
    const uniqueKey = `${incubatorName}:${position}`;
    const isDragOver = overPosition === uniqueKey;

    if (!slot || !slot.isOccupied) {
      const isDisabled = slot?.isDisabled === true;
      return (
        <DroppableSlot id={uniqueKey} isDragOver={isDragOver && !isDisabled}>
          <div
            className={`group relative flex ${MAP_SLOT_HEIGHT_CLASS} items-center justify-center p-2 ${
              isDisabled ? 'bg-gray-200 opacity-60' : ''
            }`}
          >
            <span
              className={`text-xs ${isDisabled ? 'font-semibold text-gray-500 line-through' : 'text-gray-400'}`}
            >
              {isDisabled
                ? t('fertWork.slotDisabled', 'Disabled')
                : isDragOver
                  ? t('fertWork.dropHere', 'Drop here')
                  : position}
            </span>
            {slot && (
              <button
                type="button"
                onClick={() => handleToggleSlotDisabled(slot.id)}
                disabled={isTogglingSlot}
                title={
                  isDisabled
                    ? t('fertWork.enableSlot', 'Re-enable this slot')
                    : t(
                        'fertWork.disableSlot',
                        'Disable this slot (excludes it from capacity)',
                      )
                }
                className="absolute top-1 right-1 hidden h-6 w-6 items-center justify-center rounded-full bg-white/90 text-gray-600 shadow-sm ring-1 ring-gray-300 transition-opacity group-hover:flex hover:bg-gray-100 hover:text-gray-800"
              >
                <i
                  className={`pi ${isDisabled ? 'pi-check' : 'pi-ban'} text-xs`}
                />
              </button>
            )}
          </div>
        </DroppableSlot>
      );
    }

    const isDragging = activeSlot?.id === slot.id;

    return (
      <DraggableSlot id={slot.id} slot={slot} dataTour={dataTour}>
        <DroppableSlot id={uniqueKey} isDragOver={isDragOver && !isDragging}>
          <SlotContent
            slot={slot}
            isDragging={isDragging}
            slotHeightClass={MAP_SLOT_HEIGHT_CLASS}
            isCommercial={isCommercial}
          />
        </DroppableSlot>
      </DraggableSlot>
    );
  };

  const renderGrid = (
    rows: string[],
    columns: number[],
    slots: IncubatorMapSlot[],
    incubatorName: string,
    tagFirstSlot = false,
  ) => {
    let tagged = false;

    const slotsByPosition = slots.reduce(
      (acc, slot) => {
        acc[slot.slotPosition] = slot;
        return acc;
      },
      {} as Record<string, IncubatorMapSlot>,
    );

    return (
      <div className="overflow-x-auto">
        <div className="rounded-md border-2 border-gray-400 bg-white p-2">
          <div className="grid min-w-[300px] gap-2 pl-6">
            {rows.map((row, rowIndex) => (
              <div
                key={row}
                className="grid gap-1 sm:gap-2"
                style={{
                  gridTemplateColumns: `repeat(${columns.length}, minmax(0, 1fr))`,
                }}
              >
                {columns.map((col) => {
                  const position = `${row}${col}`;
                  const slot = slotsByPosition[position];
                  const tourAttr =
                    tagFirstSlot && !tagged && slot?.isOccupied
                      ? 'maps-plate-handle'
                      : undefined;
                  if (tourAttr) tagged = true;
                  return (
                    <div key={position} className="relative">
                      {rowIndex === 0 && (
                        <div className="mb-1 border-b border-gray-300 pb-1 text-center text-sm font-bold text-gray-700">
                          {col}
                        </div>
                      )}
                      <div className="absolute top-1/2 -left-6 -translate-y-1/2 text-sm font-bold text-gray-700">
                        {col === 1 && row}
                      </div>
                      {renderSlot(slot, position, incubatorName, tourAttr)}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const renderHeraCell = (
    slots: IncubatorMapSlot[],
    incubatorName: string,
    tagFirstSlot = false,
  ) =>
    renderGrid(
      ['A', 'B', 'C', 'D', 'E'],
      [1, 2, 3, 4, 5],
      slots,
      incubatorName,
      tagFirstSlot,
    );

  const renderGridType = (
    typeName: string,
    slots: IncubatorMapSlot[],
    incubatorName: string,
    tagFirstSlot = false,
  ) => {
    const { rows, cols } = GRID_DIMENSIONS[typeName];
    return renderGrid(
      Array.from({ length: rows }, (_, i) => String.fromCharCode(65 + i)),
      Array.from({ length: cols }, (_, i) => i + 1),
      slots,
      incubatorName,
      tagFirstSlot,
    );
  };

  const renderAstec = (
    slots: IncubatorMapSlot[],
    incubatorName: string,
    tagFirstSlot = false,
  ) => {
    const chambers = Array.from({ length: 10 }, (_, i) => i + 1);
    let tagged = false;

    const slotsByPosition = slots.reduce(
      (acc, slot) => {
        acc[slot.slotPosition] = slot;
        return acc;
      },
      {} as Record<string, IncubatorMapSlot>,
    );

    return (
      <div>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 sm:gap-4 md:grid-cols-5">
          {chambers.map((chamber) => (
            <div
              key={chamber}
              className="rounded-md border-2 border-gray-400 bg-white p-2"
            >
              <div className="mb-2 border-b border-gray-300 pb-1 text-center text-sm font-bold text-gray-700">
                {t('fertWork.chamber', 'Chamber {number}', { number: chamber })}
              </div>
              <div className="flex flex-col gap-2">
                {[1, 2].map((chamberSlot) => {
                  const position = `C${chamber}-${chamberSlot}`;
                  const slot = slotsByPosition[position];
                  const tourAttr =
                    tagFirstSlot && !tagged && slot?.isOccupied
                      ? 'maps-plate-handle'
                      : undefined;
                  if (tourAttr) tagged = true;
                  return (
                    <div key={position}>
                      {renderSlot(slot, position, incubatorName, tourAttr)}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderFourMatingGrid = (
    slots: IncubatorMapSlot[],
    incubatorName: string,
    tagFirstSlot = false,
  ) => {
    // Pair consecutive occupied slots: each pair forms one 4-mating plate
    const occupiedSlots = slots
      .filter((s) => s.isOccupied)
      .sort((a, b) => a.displayOrder - b.displayOrder);
    const pairs: { primary: IncubatorMapSlot; secondary?: IncubatorMapSlot }[] =
      [];
    for (let i = 0; i < occupiedSlots.length; i += 2) {
      pairs.push({
        primary: occupiedSlots[i],
        secondary: occupiedSlots[i + 1],
      });
    }

    let tagged = false;
    return (
      <div className="overflow-x-auto">
        <div className="rounded-md border-2 border-gray-400 bg-white p-2">
          <div className="grid min-w-[300px] grid-cols-2 gap-2 sm:grid-cols-4">
            {pairs.map((pair) => {
              const tourAttr =
                tagFirstSlot && !tagged ? 'maps-plate-handle' : undefined;
              if (tourAttr) tagged = true;
              return (
                <div key={pair.primary.id}>
                  <SlotContent
                    slot={pair.primary}
                    secondSlot={pair.secondary}
                    isDragging={false}
                    slotHeightClass="min-h-[132px] sm:min-h-[148px]"
                    isCommercial={isCommercial}
                    fourMating
                  />
                </div>
              );
            })}
            {pairs.length === 0 && (
              <div className="col-span-full py-4 text-center text-sm text-gray-400">
                {t('fertWork.noOccupiedSlots', 'No occupied slots')}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  if (!sessionId) {
    return (
      <div className="p-4">
        <PageStatusBanner
          type="error"
          message={t('fertWork.noSessionIdProvided', 'No session ID provided')}
        />
      </div>
    );
  }

  return (
    <div
      className={`generate-incubator-maps-page relative flex flex-auto flex-col ${className}`}
    >
      <div className="flex flex-col gap-4">
        {mapData?.mapDate && (
          <div className="flex items-center gap-2 text-sm">
            <span className="font-medium text-gray-600">
              {t('fertWork.mapDate', 'Map Date')}:
            </span>
            <span className="font-semibold text-gray-900">
              {formatUtcDateAsLocal(mapData.mapDate)}
            </span>
          </div>
        )}
        <div
          data-tour="maps-actions"
          ref={actionsRowRef}
          className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-4"
        >
          <div className="flex flex-wrap items-center gap-2">
            {mapData && (
              <>
                <RegenerateMapsDropdown
                  onRegenerate={handleSettingsRegenerate}
                  isLoading={isGenerating}
                  labelCount={mapData.occupiedSlots ?? 0}
                  sessionId={sessionId}
                />
                <Button
                  onClick={handlePrintMap}
                  disabled={isDownloadingPdf}
                  className="text-sm"
                >
                  <i
                    className={
                      isDownloadingPdf ? 'pi pi-spin pi-spinner' : 'pi pi-print'
                    }
                    data-icon="inline-start"
                  />
                  {t('fertWork.print', 'Print')}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-gray-600"
                  onClick={() => setShowRules(true)}
                >
                  <i className="pi pi-list" data-icon="inline-start" />
                  {t('fertWork.incubatorRules', 'Incubator Rules')}
                </Button>
              </>
            )}
          </div>
          {mapData && (
            <div className="hidden items-center gap-2 text-sm text-gray-500 sm:flex">
              <i className="pi pi-info-circle" />
              <span>
                {t(
                  'fertWork.dragPlatesToRearrange',
                  'Drag plates to rearrange',
                )}
              </span>
            </div>
          )}
        </div>

        {(error || generateError) && (
          <PageStatusBanner
            type="error"
            message={
              (generateError as any)?.data ||
              (error as any)?.data ||
              t(
                'fertWork.failedToLoadGenerateMap',
                'Failed to load/generate map',
              )
            }
          />
        )}

        {(isLoading || isGenerating) && (
          <PageStatusBanner
            type="loading"
            message={
              isGenerating
                ? t('fertWork.generatingMaps', 'Generating maps...')
                : t('fertWork.loadingMaps', 'Loading maps...')
            }
          />
        )}

        {mapData && (
          <DndContext
            sensors={sensors}
            onDragStart={handleDragStart}
            onDragOver={handleDragOver}
            onDragEnd={handleDragEnd}
            onDragCancel={handleDragCancel}
          >
            <Dialog open={showRules} onOpenChange={setShowRules}>
              <DialogContent className="md:max-w-lg">
                <DialogHeader>
                  <DialogTitle>
                    {t('fertWork.incubatorRules', 'Incubator Rules')}
                  </DialogTitle>
                </DialogHeader>
                <DialogBody>
                  <ul className="ml-6 list-disc space-y-1 text-sm text-gray-700">
                    <li>
                      {t(
                        'fertWork.day5Isolated',
                        'Day 5 plates are automatically isolated on separate maps',
                      )}
                    </li>
                    <li>
                      {t(
                        'fertWork.exportIsolated',
                        'Export plates are automatically isolated on separate maps',
                      )}
                    </li>
                    <li>
                      {t(
                        'fertWork.standardPlatesShared',
                        'Standard plates (Freeze Only, Ship) share maps together',
                      )}
                    </li>
                    <li>
                      {t(
                        'fertWork.heraCellAndAstec',
                        'You can add both HeraCell and Astec incubators to the same session',
                      )}
                    </li>
                  </ul>
                </DialogBody>
              </DialogContent>
            </Dialog>

            <div
              data-tour="maps-stats"
              className="grid grid-cols-3 gap-2 sm:gap-4"
            >
              <div className="rounded-lg border border-gray-200 bg-white p-2 sm:p-4">
                <div className="mb-1 text-xs text-gray-500">
                  {t('fertWork.total', 'TOTAL')}
                </div>
                <div className="text-lg font-bold sm:text-2xl">
                  {mapData.totalSlots}
                </div>
                <div className="hidden text-xs text-gray-500 sm:block">
                  {(() => {
                    const typeCounts = mapData.incubators.reduce(
                      (acc, inc) => {
                        acc[inc.incubatorType] =
                          (acc[inc.incubatorType] || 0) + 1;
                        return acc;
                      },
                      {} as Record<string, number>,
                    );
                    return Object.entries(typeCounts)
                      .map(([type, count]) => `${count} ${type}`)
                      .join(' + ');
                  })()}
                </div>
              </div>
              <div className="rounded-lg border border-gray-200 bg-white p-2 sm:p-4">
                <div className="mb-1 text-xs text-gray-500">
                  {t('fertWork.occupied', 'OCCUPIED')}
                </div>
                <div className="text-lg font-bold text-blue-600 sm:text-2xl">
                  {mapData.occupiedSlots}
                </div>
              </div>
              <div className="rounded-lg border border-gray-200 bg-white p-2 sm:p-4">
                <div className="mb-1 text-xs text-gray-500">
                  {t('fertWork.available', 'AVAILABLE')}
                </div>
                <div className="text-lg font-bold text-green-600 sm:text-2xl">
                  {mapData.availableSlots}
                </div>
              </div>
            </div>

            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <div className="mb-2 text-sm font-medium">
                {t('fertWork.colorLegend', 'Color Legend')}
              </div>
              <div className="flex flex-wrap gap-4">
                {EMBRYO_CATEGORY_COLOR_KEY_ITEMS.filter(
                  (item) => item.key !== 'halfPlateDiffSires',
                ).map((item) => (
                  <div key={item.key} className="flex items-center gap-2">
                    <div
                      className={`h-4 w-4 rounded border ${item.colorClass}`}
                    ></div>
                    <span className="text-sm">
                      {t(
                        item.key === 'export'
                          ? 'fertWork.exportAuEuUkSa'
                          : item.translationKey,
                      )}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <div data-tour="maps-incubators" className="flex flex-col gap-4">
              {mapData.incubators.map((incubator, incubatorIndex) => {
                const incubatorSlots = mapData.slots.filter(
                  (s) => s.incubatorName === incubator.incubatorName,
                );
                return (
                  <div
                    key={incubator.incubatorName}
                    className="rounded-lg border border-gray-200 bg-white p-4"
                  >
                    <div className="mb-4 flex items-center justify-between">
                      <h2 className="text-lg font-medium">
                        {incubator.incubatorName}
                      </h2>
                      <div className="flex items-center gap-3">
                        <span className="text-sm text-gray-500">
                          {t(
                            'fertWork.occupiedCount',
                            '{occupied}/{total} occupied',
                            {
                              occupied: incubator.occupiedSlots,
                              total: incubator.totalSlots,
                            },
                          )}
                        </span>
                        <Tooltip>
                          <TooltipTrigger
                            render={
                              <Button
                                variant="ghost"
                                size="icon-sm"
                                className="!p-1"
                                onClick={() =>
                                  handlePrintSingleMap(incubator.incubatorName)
                                }
                                disabled={
                                  printingIncubator === incubator.incubatorName
                                }
                              >
                                <i
                                  className={
                                    printingIncubator ===
                                    incubator.incubatorName
                                      ? 'pi pi-spin pi-spinner'
                                      : 'pi pi-print'
                                  }
                                />
                                <span className="sr-only">
                                  {t(
                                    'fertWork.printThisIncubator',
                                    'Print this incubator',
                                  )}
                                </span>
                              </Button>
                            }
                          />
                          <TooltipContent side="top">
                            {t(
                              'fertWork.printThisIncubator',
                              'Print this incubator',
                            )}
                          </TooltipContent>
                        </Tooltip>
                      </div>
                    </div>
                    {matingsPerPlate === 4
                      ? renderFourMatingGrid(
                          incubatorSlots,
                          incubator.incubatorName,
                          incubatorIndex === 0,
                        )
                      : incubator.incubatorType === 'HeraCell'
                        ? renderHeraCell(
                            incubatorSlots,
                            incubator.incubatorName,
                            incubatorIndex === 0,
                          )
                        : incubator.incubatorType in GRID_DIMENSIONS
                          ? renderGridType(
                              incubator.incubatorType,
                              incubatorSlots,
                              incubator.incubatorName,
                              incubatorIndex === 0,
                            )
                          : renderAstec(
                              incubatorSlots,
                              incubator.incubatorName,
                              incubatorIndex === 0,
                            )}
                  </div>
                );
              })}
            </div>

            <DragOverlay>
              {activeSlot ? (
                <div className="opacity-90">
                  <SlotContent
                    slot={activeSlot}
                    isDragging={false}
                    slotHeightClass={MAP_SLOT_HEIGHT_CLASS}
                    isCommercial={isCommercial}
                  />
                </div>
              ) : null}
            </DragOverlay>
          </DndContext>
        )}

        {showFab && mapData && (
          <div className="group fixed right-4 bottom-4 z-50 flex flex-col-reverse items-center gap-2 sm:right-6 sm:bottom-6 sm:gap-3">
            <Tooltip>
              <TooltipTrigger
                render={
                  <Button
                    size="icon"
                    className="!h-12 !w-12 rounded-full shadow-lg sm:!h-14 sm:!w-14"
                    onClick={handlePrintMap}
                    disabled={isDownloadingPdf}
                  >
                    <i
                      className={
                        isDownloadingPdf
                          ? 'pi pi-spin pi-spinner'
                          : 'pi pi-print'
                      }
                    />
                    <span className="sr-only">
                      {t('fertWork.print', 'Print')}
                    </span>
                  </Button>
                }
              />
              <TooltipContent side="left">
                {t('fertWork.print', 'Print')}
              </TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger
                render={
                  <Button
                    variant="warning"
                    size="icon"
                    className="!h-9 !w-9 translate-y-4 rounded-full opacity-0 shadow-lg transition-all duration-200 group-hover:translate-y-0 group-hover:opacity-100 sm:!h-10 sm:!w-10"
                    onClick={() => {
                      const { incubatorCounts, preferenceOrder } =
                        getStoredIncubatorSelection();
                      handleSettingsRegenerate(
                        incubatorCounts,
                        preferenceOrder,
                      );
                    }}
                    disabled={isGenerating}
                  >
                    <i
                      className={
                        isGenerating ? 'pi pi-spin pi-spinner' : 'pi pi-refresh'
                      }
                    />
                    <span className="sr-only">
                      {t('fertWork.regenerate', 'Regenerate')}
                    </span>
                  </Button>
                }
              />
              <TooltipContent side="left">
                {t('fertWork.regenerate', 'Regenerate')}
              </TooltipContent>
            </Tooltip>
          </div>
        )}

        {!mapData && !isLoading && !isGenerating && !error && (
          <PageStatusBanner
            type="empty"
            message={t(
              'fertWork.noIncubatorMapGenerated',
              'No incubator map generated yet. Go back and set incubator counts to generate maps.',
            )}
          />
        )}
      </div>
    </div>
  );
};
